import { useCallback, useEffect, useMemo, useState } from "react";

import { useHomepageSsr } from "@/providers/HomepageSsrProvider";

type Countdown = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

// Original event date (keep as canonical source)
const REAL_EVENT_DATE_ISO = "2025-11-04T19:00:00-05:00";

const getEventDateMs = (
  preferredTargetMs: number | undefined,
  search: string,
  nowMs: number
): number => {
  try {
    const params = new URLSearchParams(search);
    const override = params.get("eventDate");

    if (override) {
      const asNumber = Number(override);
      if (!Number.isNaN(asNumber)) return asNumber;

      const parsed = Date.parse(override);
      if (!Number.isNaN(parsed)) return parsed;
    }
  } catch {
    // ignore and continue to fallback logic
  }

  if (
    typeof preferredTargetMs === "number" &&
    Number.isFinite(preferredTargetMs)
  ) {
    return preferredTargetMs;
  }

  const real = Date.parse(REAL_EVENT_DATE_ISO);

  if (real <= nowMs) {
    const threeDaysMs = 3 * 24 * 60 * 60 * 1000;
    return nowMs + threeDaysMs;
  }

  return real;
};

const padTime = (value: number): string => value.toString().padStart(2, "0");

const useCountdown = (targetMs: number, initialNowMs: number): Countdown => {
  const calculate = useCallback(
    (nowMs = Date.now()): Countdown => {
      const diff = Math.max(targetMs - nowMs, 0);
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      return { days, hours, minutes, seconds };
    },
    [targetMs]
  );

  const [timeLeft, setTimeLeft] = useState<Countdown>(() =>
    calculate(initialNowMs)
  );

  useEffect(() => {
    setTimeLeft(calculate());
    const intervalId = window.setInterval(() => {
      setTimeLeft(calculate());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [calculate]);

  return timeLeft;
};

const getRequestSearch = (initialPath?: string): string => {
  if (typeof window !== "undefined") {
    return window.location.search;
  }

  const queryIndex = initialPath?.indexOf("?") ?? -1;
  return queryIndex >= 0 ? initialPath?.slice(queryIndex) ?? "" : "";
};

export const useLaunchCountdown = (preferredTargetMs?: number) => {
  const { initialPath, launch: ssrLaunch } = useHomepageSsr();
  const [initialNowMs] = useState(() => ssrLaunch?.renderedAtMs ?? Date.now());
  const search = getRequestSearch(initialPath);
  const targetDateMs = useMemo(
    () => getEventDateMs(preferredTargetMs, search, initialNowMs),
    [initialNowMs, preferredTargetMs, search]
  );
  const countdown = useCountdown(targetDateMs, initialNowMs);

  const countdownLabel = useMemo(() => {
    return `${countdown.days}d : ${padTime(countdown.hours)}h : ${padTime(
      countdown.minutes
    )}m : ${padTime(countdown.seconds)}s`;
  }, [countdown]);

  return { countdown, countdownLabel };
};
